<template>
  <div class="mb-5">
    <ValidationObserver
      @submit.prevent="signup"
      tag="form"
      class="container"
      ref="form"
      v-slot="{ invalid }"
    >
      <div class="row py-5">
        <div class="col-12 text-center mt-4" v-if="logo">
          <img :src="logo" :alt="title" class="img-fluid header-logo" />
        </div>
        <div class="col-12 mt-4">
          <hr />
        </div>
      </div>
      <div class="row">
        <div class="col-12 col-lg-9 pr-lg-5">
          <div class="row" v-if="userPlans.length">
            <div class="col-12">
              <h1>Payment options</h1>
              <div class="plans">
                <ProgrammePlanCard
                  :color="themeColor"
                  @click.native="selectPlan(plan.option_id)"
                  :value="selectedPlanId"
                  :plan="plan"
                  v-for="plan in plansData"
                  :key="plan.id"
                />
              </div>
            </div>
          </div>
          <div class="row mt-5" v-if="!teacher">
            <div class="col-md-12">
              <h1>Create your account</h1>
              <p v-if="!isLoggedIn">
                Already have a Marvelous account?
                <a href @click.prevent="openLoginModal">Click here to login.</a>
              </p>
            </div>
            <div class="col-md-12 mt-3">
              <HMTextInput
                rules="required|min:3"
                autocomplete="organization"
                :label="$t('GENERAL.LABEL.VIRTUAL_STUDIO_NAME')"
                :placeholder="$t('SIGNUP.TEXT.DONT_WORRY_YOU_CAN')"
                v-model="teacherForm.studio_name"
                @focusout.native="validateStudioName"
                :infoError="URLInfo"
              />
            </div>
            <template v-if="!isLoggedIn">
              <div class="col-md-6">
                <HMTextInput
                  rules="required"
                  autocomplete="given-name"
                  :label="$t('GENERAL.LABEL.FIRST_NAME')"
                  v-model="teacherForm.first_name"
                />
              </div>
              <div class="col-md-6">
                <HMTextInput
                  rules="required"
                  autocomplete="family-name"
                  :label="$t('GENERAL.LABEL.LAST_NAME')"
                  v-model="teacherForm.last_name"
                />
              </div>
              <div class="col-md-12 mb-4">
                <HMTextInput
                  class="mb-0"
                  rules="required"
                  type="email"
                  autocomplete="email"
                  ref="email"
                  vid="email"
                  :label="$t('GENERAL.LABEL.EMAIL')"
                  v-model.trim="teacherForm.email"
                  :infoError="emailInfo"
                  :placeholder="
                    $t('SIGNUP.TEXT.FOR_RENEWAL_NOTICES_LOVE_LETTERS_ETC')
                  "
                />
                <span class="d-block mt-2" v-if="isUsedEmail">
                  <span class="invalid-feedback d-block">
                    {{ $t('GENERAL.ERROR.A_USER_IS_ALREADY_REGISTERED') }}
                  </span>
                  <span
                    @click="openLoginModal"
                    v-html="sanitize($t('SIGNUP.ERROR.PLEASE_CLICK_HERE_TO'))"
                  ></span>
                </span>
              </div>
              <div class="col-md-6">
                <HMTextInput
                  rules="required|min:8"
                  type="password"
                  name="password"
                  :label="$t('GENERAL.LABEL.PASSWORD')"
                  v-model="teacherForm.password"
                  autocomplete="current-password"
                />
              </div>
              <div class="col-md-6">
                <HMTextInput
                  rules="required|min:8|password:@password"
                  type="password"
                  :label="$t('GENERAL.LABEL.REPEAT_PASSWORD')"
                  v-model="teacherForm.password_repeat"
                  autocomplete="current-password"
                />
              </div>
            </template>
          </div>
          <div class="row mt-5" v-else>
            <div class="col-md-12">
              <h1>Existing account information</h1>
            </div>
            <div class="col-md-12 mt-3 form-group">
              <div>
                You are logged in as
                <span class="h4">{{ teacher.studio_name }}</span>
              </div>
            </div>
          </div>
          <div class="row mt-5" v-if="!teacher">
            <div class="col-md-12">
              <h1>{{ $t('SIGNUP.TITLE.TIMEZONE_INFORMATION') }}</h1>
            </div>
            <div class="col-md-12 mt-3 form-group">
              <v-select
                :options="timezones"
                label="name"
                code="value"
                v-model="timezone"
                :clearable="false"
              />
            </div>
          </div>
          <div class="row mt-5" v-if="showCoupon">
            <div class="col">
              <h1>Coupon code</h1>
              <div class="form-group mt-3">
                <label for="coupon">
                  {{ $t('SIGNUP.LABEL.COUPON_CODE') }}
                </label>
                <div
                  class="d-flex"
                  :class="{ 'is-invalid': couponError || isCouponValid }"
                >
                  <input
                    type="text"
                    class="form-control"
                    name="coupon"
                    id="coupon"
                    v-model="coupon"
                    @change="removeCoupon"
                  />
                  <span
                    class="glyphicon glyphicon-ok form-control-feedback"
                    v-if="usingCoupon"
                    aria-hidden="true"
                  ></span>
                  <HMButton
                    class="ml-4 text-uppercase"
                    :disabled="!coupon || !isCouponValid"
                    color="charcoal"
                    @click="applyCoupon"
                  >
                    {{ $t('SIGNUP.BUTTON.APPLY') }}
                  </HMButton>
                </div>
                <span class="invalid-feedback" v-if="couponError">
                  {{ couponError }}
                </span>
              </div>
            </div>
          </div>
          <div class="row mt-5">
            <div class="col form-group">
              <h1>
                {{ $t('SIGNUP.TITLE.BILLING_INFORMATION') }}
              </h1>
              <label class="mt-3" for="card-element">
                {{ $t('SIGNUP.LABEL.CREDIT_CARD') }}
              </label>
              <div
                id="card-element"
                ref="card-element"
                :class="{ 'is-invalid': stripeError }"
              ></div>
              <div class="invalid-feedback" v-if="stripeError">
                {{ stripeError }}
              </div>
            </div>
          </div>
          <div class="row submit-group mt-5" v-if="selectedPlan">
            <div class="col-md-12">
              <div class="d-flex mt-3 justify-content-between">
                <span class="h2">Total</span>
                <span class="h2">
                  {{ selectedPlan.fullAmountText }}
                </span>
              </div>
              <div class="text-right small" v-if="selectedPlan.isPaymentPlan">
                <span class="c-charcoal">{{ selectedPlan.description }}</span>
              </div>
            </div>
            <div class="col-md-12 text-right">
              <small>
                <i>{{ $t('SIGNUP.TEXT.ALL_PRICES_ARE_IN_US_DOLLARS') }}</i>
              </small>
            </div>

            <div class="col-md-12">
              <span
                class="d-block"
                v-if="usingCoupon"
                v-html="
                  sanitize($t('SIGNUP.TEXT.COUPON_CODE_APPLIED', { coupon }))
                "
              >
              </span>
              <hr class="submit-hr" />
            </div>
            <div class="col-md-12">
              <HMButton
                class="w-100"
                :class="{
                  disabled:
                    !stripeCompleted || !timezone || invalid || inProgress,
                }"
                color="charcoal"
                type="submit"
                :loading="inProgress"
              >
                <span class="w-100">{{ $t(ctaButton) }}</span>
              </HMButton>
            </div>
            <div class="col-md-12">
              <span
                class="text-center d-block my-3"
                v-html="
                  sanitize(
                    $t('SIGNUP.TEXT.BY_CLICKING_THE_BUTTON_ABOVE', {
                      termsOfUse: config.termsOfUseURL,
                      privacyPolicy: config.privacyPolicyURL,
                      applicationName,
                    })
                  )
                "
              ></span>
            </div>
          </div>
          <div class="mx-3 mt-3">
            <font-awesome-icon :icon="['far', 'lock-alt']" class="mr-1" />
            {{ $t('SIGNUP.TEXT.THIS_IS_A_SECURE_256_BIT') }}
            <span
              class="d-block mt-1 small"
              v-html="sanitize($t('SIGNUP.TEXT.THIS_SITE_IS_PROTECTED_BY'))"
            ></span>
          </div>
          <div class="mx-3 mt-5">
            <slot name="testimonials"></slot>
          </div>
        </div>
        <div class="col-12 col-lg-3">
          <slot name="sidebar"></slot>
        </div>
      </div>
      <div class="row footer">
        <div class="col text-center">
          <img
            src="/assets/images/marvelous-logo-color-underline.svg"
            class="mb-3"
          />
          <div class="smaller">
            Copyright {{ currentYear }} Marvelous®. All Rights Reserved
          </div>
        </div>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import { ValidationObserver } from 'vee-validate';
import Vue from 'vue';
import { debounce, isObject } from 'lodash';

import { getCoachingProgrammePlansData } from '@/web/utils/plans';
import ProgrammePlanCard from '@/web/components/signup/ProgrammePlanCard.vue';
import { mapGetters } from 'vuex';
import auth from '@/core/api/modules/auth';
import { style } from '@/web/utils/stripe';
import { trackPurchase, register, track } from '@/web/services/analytics';
import { errorToMessage, scrollToFirstFormError } from '@/core/utils/errors';
import { loadDeadlineFunnelScript } from '@/web/utils/deadlinefunnel';
import timezones from '@/web/utils/timezones.ts';
import { getConfig } from '@/web/utils/configuration';
import StudentLogin from '@/web/components/signup/StudentLogin.vue';
import { sanitize } from '@/core/utils/utils';
import { getTrackingParams } from '@/web/utils/tracking';

export default Vue.extend({
  components: { ProgrammePlanCard, ValidationObserver },
  props: {
    title: {
      type: String,
      default: null,
    },
    programmeCode: {
      type: String,
      default: null,
    },
    ctaButton: {
      type: String,
      default: 'SIGNUP.BUTTON.GET_STARTED',
    },
    themeColor: {
      type: String,
      default: 'lwi',
    },
    logo: {
      type: String,
      default: null,
    },
    sale: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('user', [
      'userId',
      'studentId',
      'teacher',
      'user',
      'programme',
      'isLoggedIn',
    ]),
    ...mapGetters({
      affiliateCode: 'globals/affiliateCode',
      affiliateCoupon: 'affiliates/affiliateCoupon',
    }),
    selectedPlan() {
      return this.plansData.find(
        plan => plan.option_id === this.selectedPlanId
      );
    },
    sortedPaymentPlans() {
      if (!this.programme) return [];

      let plans = [...this.programme.payment_plans];
      if (this.sale) {
        plans = plans.filter(p => p.display_on_sale_page);
      }

      return plans.sort(
        (plan1, plan2) =>
          plan1.number_of_subsequent_payments -
          plan2.number_of_subsequent_payments
      );
    },
    userPlans() {
      if (!this.programme) return [];

      const plans = [];
      this.sortedPaymentPlans.forEach(plan => {
        plans.push({
          option_id: plans.length,
          display_name: `${plan.number_of_subsequent_payments + 1} payments`,
          plan_name: plan.plan_name,
          initial_payment_amount: plan.initial_payment_amount,
          subsequent_payment_amount: plan.subsequent_payment_amount,
          number_of_subsequent_payments: plan.number_of_subsequent_payments,
          student_accounts: plan.student_accounts,
          storage: plan.storage,
          teachers: plan.teachers,
          max_admins: plan.max_admins,
          allows_custom_domain: plan.allows_custom_domain,
          campaign: plan.campaign,
          is_default: plan.number_of_subsequent_payments === 0,
          description: this.getPaymentPlanText(plan),
          streaming_minutes: plan.streaming_minutes,
          can_access_deadline_funnel: plan.can_access_deadline_funnel,
          is_payment_plan: true,
          payment_plan_id: plan.id,
          tier: plan.number_of_subsequent_payments,
        });
      });

      return plans;
    },
    plansData() {
      return [...(this.userPlans || [])]
        .filter(plan => !(plan.hide_for_affiliates && this.affiliateCode))
        .sort((planA, planB) => planA.tier - planB.tier)
        .map(plan => getCoachingProgrammePlansData(plan));
    },
    config() {
      return getConfig();
    },
    applicationName() {
      return this.config.applicationName;
    },
    isNamastreamDomain() {
      return window.location.host.toLowerCase().indexOf('namastream') !== -1;
    },
    currentYear() {
      return new Date().getFullYear();
    },
    isYogaAllianceCoupon() {
      if (!this.coupon) return false;

      return this.coupon.toLowerCase().startsWith('ya');
    },
    isWeTravelCoupon() {
      if (!this.coupon) return false;

      return this.coupon.toLowerCase().startsWith('wet');
    },
    isBeYogiCoupon() {
      if (!this.coupon) return false;

      return this.coupon.toLowerCase().startsWith('bey');
    },
    isCouponValid() {
      if (!this.coupon) return false;
      if (!this.programmeCode) return true;

      return (
        this.coupon
          .toLowerCase()
          .startsWith(this.programmeCode.toLowerCase()) ||
        this.isYogaAllianceCoupon ||
        this.isWeTravelCoupon ||
        this.isBeYogiCoupon
      );
    },
  },
  data() {
    return {
      subscriptionType: 'yearly',
      allPlansInclude: [
        'SIGNUP.TEXT.LIVE_STREAMING',
        'SIGNUP.TEXT.SET_YOUR_PRICING',
        'SIGNUP.TEXT.OFFER_COACHING',
        'SIGNUP.TEXT.NO_REV_SHARE',
        'SIGNUP.TEXT.SELL_COURSES',
        'SIGNUP.TEXT.INSTANT_PAYOUTS',
      ],
      card: null,
      selectedPlanId: 0,
      teacherForm: {
        yoga_alliance_membership_id: '',
        studio_name: '',
        first_name: '',
        last_name: '',
        email: '',
        password: '',
        password_repeat: '',
      },
      usingCoupon: false,
      showCoupon: false,
      coupon: null,
      couponError: null,
      inProgress: false,
      stripeCompleted: false,
      stripeError: false,
      emailInfo: null,
      URLInfo: null,
      timezone: null,
      timezones,
      isUsedEmail: false,
    };
  },
  methods: {
    sanitize,
    openLoginModal() {
      this.$modal.show(StudentLogin, {});
    },
    getPaymentPlanText(plan) {
      if (plan.number_of_subsequent_payments > 0) {
        return `Pay $${plan.initial_payment_amount} today, followed by  ${plan.number_of_subsequent_payments} payments of $${plan.subsequent_payment_amount} every ${plan.billing_frequency_days} days`;
      }

      return `Full $${plan.initial_payment_amount} payment`;
    },
    selectPlan(planOptionId) {
      this.selectedPlanId = planOptionId;
      const { id, name } = this.selectedPlan || {};
      this.$trackOnPage('lwi teacher switched payment plan', {
        plan_id: id,
        plan_name: name,
      });
    },
    removeCoupon() {
      this.$store.dispatch('user/clearCoupon');
    },
    async validateStudioName() {
      // can't validate studio url without auth headers of an existing account... how to do this?
      console.log('validation needed');
    },
    async applyCoupon() {
      try {
        const coupon = this.coupon;
        const selectedOptionIndex = this.plansData.indexOf(this.selectedPlan);
        await this.$store.dispatch('user/applyProgrammeCoupon', {
          code: this.programmeCode,
          coupon,
        });
        if (selectedOptionIndex >= 0) {
          this.selectedPlanId = this.plansData[selectedOptionIndex].option_id;
        }
        this.couponError = null;
        this.usingCoupon = true;
      } catch (error) {
        this.couponError = error.response.data?.message;
        this.usingCoupon = false;
      }
    },
    stripeChangeCallback(event) {
      if (event.complete) {
        this.stripeCompleted = true;
        this.stripeError = false;
      } else if (event.error) {
        this.stripeCompleted = false;
        this.stripeError = event.error.message;
      }
    },
    initStripe() {
      this.stripe = window.Stripe(process.env.VUE_APP_STRIPE_PUBLIC);
      const elements = this.stripe.elements();
      this.card = elements.create('card', {
        style,
        hidePostalCode: true,
      });
      this.$nextTick(() => {
        this.card.mount(this.$refs['card-element']);
        this.card.addEventListener('change', this.stripeChangeCallback);
      });
    },
    async signup() {
      this.$refs.form.validate();
      const invalid = this.$refs.form?.flags?.invalid;
      if (invalid) {
        setTimeout(() => {
          scrollToFirstFormError();
        });
      }

      if (
        !this.stripeCompleted ||
        !this.timezone ||
        invalid ||
        this.inProgress
      ) {
        return;
      }

      const reCaptchaKey = process.env.VUE_APP_RECAPTCHA_KEY;
      let reCaptchaToken;
      try {
        reCaptchaToken = await window.grecaptcha.execute(reCaptchaKey, {
          action: 'teachersetupintent',
        });
      } catch (error) {
        this.inProgress = false;
        this.$notify({
          group: 'app',
          type: 'error',
          text: this.$t('GENERAL.ERROR.RECAPTCHA_FAILED'),
        });
      }

      this.inProgress = true;
      const data = {
        ...this.teacherForm,
        ...getTrackingParams(),
        coupon: this.coupon,
        campaign: 'heymarvelous',
        plan_id: this.programme.plan.id,
        subscription_type: this.subscriptionType,
        password1: this.teacherForm.password_repeat,
        timezone: this.timezone.value,
        programme_id: this.programme.id,
        programme_payment_plan_id: this.selectedPlan.paymentPlanId,
        token: reCaptchaToken,
      };

      try {
        const setupIntent = await auth.getSetupIntent(data);
        data.stripe_customer_id = setupIntent.stripe_customer_id;
        data.setup_intent_id = setupIntent.setup_intent.id;

        const confirmCard = await this.stripe.confirmCardSetup(
          setupIntent.setup_intent.client_secret,
          { payment_method: { card: this.card } }
        );

        data.default_payment_method = confirmCard.setupIntent.payment_method;

        data.affiliate_ref = this.affiliateCode;

        reCaptchaToken = await window.grecaptcha.execute(reCaptchaKey, {
          action: 'teachersignup',
        });
        data.token = reCaptchaToken;

        if (this.isLoggedIn) await auth.promoteStudentToTeacher(data);
        else await auth.registerTeacher(data);

        const planAmount = this.selectedPlan[`${this.subscriptionType}_amount`];

        if (!this.isLoggedIn) await this.$store.dispatch('user/login', data);

        await this.$store.dispatch('user/loadUserInfo');

        const registerData = {
          first_name: this.teacher?.first_name,
          last_name: this.teacher?.last_name,
          email: this.teacher?.email,
          user_id: this.userId,
          student_id: this.studentId,
          studio_url: this.teacher?.studio_url,
          studio_name: this.teacher?.studio_name,
        };

        const traitsData = {
          coupon: this.coupon,
          campaign: this.campaign,
          plan_name: this.selectedPlan.plan_name,
          plan_amount: planAmount,
          subscription_type: this.subscriptionType,
          studio_name: this.teacher?.studio_name,
          programme: this.programmeCode,
        };

        register(this.userId, registerData);
        this.$trackOnPage('lwi teacher signed up', {
          ...registerData,
          ...traitsData,
        });
        trackPurchase(traitsData);

        this.$router.push({ name: 'thank-you' });
      } catch (error) {
        console.error(error);
        const errorMessage = errorToMessage(error.response);
        if (isObject(errorMessage)) {
          this.$refs.form.setErrors(errorMessage);
          scrollToFirstFormError();
        } else {
          this.$notify({
            group: 'app',
            type: 'error',
            text: errorMessage || error,
            duration: -1, // stay ON
          });
        }
        this.inProgress = false;
      }
    },
    loadScripts() {
      loadDeadlineFunnelScript();
    },
    setLocalTimeZone() {
      try {
        const localTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const timezone = this.timezones.find(
          ({ value }) => value === localTimeZone
        );
        if (timezone) this.timezone = timezone;
      } catch (error) {
        console.error(error);
      }
    },
    setBrandColor(color) {
      const html = document.querySelector('html');
      html.style.setProperty('--brand-color', color);
    },
  },
  watch: {
    'teacherForm.email': debounce(async function () {
      if (this.$refs.form.fields?.email.valid) {
        try {
          await auth.verifyEmail(this.teacherForm.email);
          this.emailInfo = null;
        } catch (error) {
          this.emailInfo = this.$t('GENERAL.ERROR.ARE_YOU_SURE_THIS_EMAIL');
        }
      } else {
        this.emailInfo = null;
      }

      if (this.$refs.form.fields?.email.valid) {
        try {
          await auth.verifyNewEmail(this.teacherForm.email);
          this.isUsedEmail = false;
        } catch (error) {
          this.isUsedEmail = true;
        }
      } else {
        this.isUsedEmail = false;
      }
    }, 400),
    user: {
      handler: function (user) {
        if (user) {
          const timezone = this.timezones.find(
            ({ value }) => value === user.timezone
          );
          this.timezone = timezone;
        }
      },
      immediate: true,
    },
  },
  async mounted() {
    if (this.isNamastreamDomain && process.env.NODE_ENV === 'production') {
      window.location.hostname = 'app.heymarvelous.com';
    }
    this.campaign = this.campaign || getConfig().signupCampaign;
    this.loadScripts();
    this.removeCoupon();
    await this.$store.dispatch('user/getCoachingProgramme', this.programmeCode);
    this.selectedPlanId = this.plansData.length
      ? this.plansData.find(pd => pd.isMostPopular).option_id
      : 0;
    this.showCoupon = 'enable-coupon' in this.$route.query;
    this.setLocalTimeZone();
    this.initStripe();
  },
  destroyed() {
    this.removeCoupon();
    this.setBrandColor(null);
  },
});
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.title {
  font-size: 4rem;
  margin-bottom: 120px;
  @include media-breakpoint-down(md) {
    font-size: 3rem;
  }
}

.plans {
  display: flex;
  justify-content: center;
  margin-top: 2rem;
  flex-direction: column;
  flex-wrap: nowrap;
}

.submit-group {
  border: 1px solid $medium-gray;
  border-radius: 6px;
  margin: 0;
}

.submit-hr {
  margin: 1.5rem -1rem;
}

.footer {
  margin-top: 150px;
}

.header-logo {
  max-width: 540px;
  @include media-breakpoint-down(sm) {
    max-width: 360px;
  }
}
</style>
